import { useRef, useState } from 'react'

import { PINE_EXAMPLES } from '../../pine/examples/index'
import { compilePine } from '../../pine/index'
import { useCustomIndicatorStore } from '../../stores/customIndicatorStore'
import type { CustomIndicator } from '../../stores/customIndicatorStore'
import { DEFAULT_PINE_CODE, usePineStore } from '../../stores/pineStore'
import type { PineScript } from '../../stores/pineStore'
import { cn } from '../../utils/cn'

import { CustomIndicatorEditor } from './CustomIndicatorEditor'
import { PineScriptEditor } from './PineScriptEditor'

type Tab = 'custom' | 'pine'

interface Props {
  onClose: () => void
  onAttachToChart?: (definitionId: string) => void
  errors?: Map<string, string[]>
}

export function CustomIndicatorPanel({ onClose, onAttachToChart, errors = new Map() }: Props) {
  const [tab, setTab] = useState<Tab>('custom')

  // ── Custom JS state ───────────────────────────────────────────────────────
  const { indicators, removeIndicator, duplicateIndicator, toggleEnabled, importIndicators } =
    useCustomIndicatorStore()
  const [editingCustom, setEditingCustom] = useState<CustomIndicator | null | 'new'>('new')
  const importCustomRef = useRef<HTMLInputElement>(null)

  // ── Pine state ────────────────────────────────────────────────────────────
  const {
    scripts,
    addScript,
    updateScript,
    removeScript,
    duplicateScript,
    toggleEnabled: togglePine,
    importScripts,
  } = usePineStore()
  const [editingPine, setEditingPine] = useState<PineScript | null>(null)
  const [showPineExamples, setShowPineExamples] = useState(false)
  const importPineRef = useRef<HTMLInputElement>(null)

  // ── Custom JS handlers ────────────────────────────────────────────────────
  const handleExportCustom = () => {
    const blob = new Blob([JSON.stringify(indicators, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `markethub-custom-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportCustom = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string) as CustomIndicator[]
        if (Array.isArray(data)) importIndicators(data)
      } catch {
        // invalid JSON
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // ── Pine handlers ─────────────────────────────────────────────────────────
  const handleExportPine = () => {
    const blob = new Blob([JSON.stringify(scripts, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `markethub-pine-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportPine = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string) as PineScript[]
        if (Array.isArray(data)) importScripts(data)
      } catch {
        // invalid JSON
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const handleNewPine = () => {
    const id = addScript('My Indicator', '', DEFAULT_PINE_CODE)
    const created = usePineStore.getState().scripts.find((s) => s.id === id)
    if (created) setEditingPine(created)
  }

  const handleLoadPineExample = (exId: string) => {
    const ex = PINE_EXAMPLES.find((e) => e.id === exId)
    if (!ex) return
    const result = compilePine(ex.code)
    const id = addScript(result.title || ex.title, ex.description, ex.code)
    const created = usePineStore.getState().scripts.find((s) => s.id === id)
    if (created) setEditingPine(created)
    setShowPineExamples(false)
  }

  const selectedCustom = editingCustom === 'new' ? null : editingCustom

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex h-[90vh] max-h-[700px] w-[95vw] max-w-[960px] overflow-hidden rounded-lg border border-tv-border bg-tv-bg-primary shadow-2xl">
        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <div className="flex w-64 shrink-0 flex-col border-r border-tv-border">
          {/* Tab bar */}
          <div className="flex border-b border-tv-border">
            <button
              type="button"
              onClick={() => setTab('custom')}
              className={cn(
                'flex-1 py-2 text-xs font-medium transition-colors',
                tab === 'custom'
                  ? 'border-b-2 border-tv-blue text-tv-blue'
                  : 'text-tv-text-muted hover:text-tv-text-primary'
              )}
            >
              Custom JS
            </button>
            <button
              type="button"
              onClick={() => setTab('pine')}
              className={cn(
                'flex-1 py-2 text-xs font-medium transition-colors',
                tab === 'pine'
                  ? 'border-b-2 border-tv-blue text-tv-blue'
                  : 'text-tv-text-muted hover:text-tv-text-primary'
              )}
            >
              Pine Script
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-2 text-tv-text-muted hover:text-tv-text-primary"
            >
              ✕
            </button>
          </div>

          {/* ── Custom JS list ──────────────────────────────────────────── */}
          {tab === 'custom' && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {indicators.length === 0 && (
                  <p className="p-4 text-center text-[10px] text-tv-text-muted">
                    No indicators yet. Create one →
                  </p>
                )}
                {indicators.map((ind) => {
                  const hasError = errors.has(ind.id)
                  const isSelected = editingCustom !== 'new' && editingCustom?.id === ind.id
                  return (
                    <div
                      key={ind.id}
                      className={cn(
                        'flex cursor-pointer flex-col gap-1 border-b border-tv-border/50 px-3 py-2 hover:bg-tv-bg-secondary',
                        isSelected && 'bg-tv-bg-secondary'
                      )}
                      onClick={() => setEditingCustom(ind)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: ind.color }}
                        />
                        <p className="min-w-0 flex-1 truncate text-xs text-tv-text-primary">
                          {ind.name}
                        </p>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            title={ind.enabled ? 'Disable' : 'Enable'}
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleEnabled(ind.id)
                            }}
                            className={`text-[10px] ${ind.enabled ? 'text-tv-green' : 'text-tv-text-muted'}`}
                          >
                            {ind.enabled ? '●' : '○'}
                          </button>
                          <button
                            type="button"
                            title="Duplicate"
                            onClick={(e) => {
                              e.stopPropagation()
                              duplicateIndicator(ind.id)
                            }}
                            className="text-[10px] text-tv-text-muted hover:text-tv-text-primary"
                          >
                            ⧉
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeIndicator(ind.id)
                            }}
                            className="text-[10px] text-tv-text-muted hover:text-tv-red"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      {hasError && <p className="text-[9px] text-tv-red">Error in code</p>}
                      {onAttachToChart && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onAttachToChart(ind.id)
                          }}
                          className="mt-0.5 w-full rounded bg-tv-blue/10 px-2 py-0.5 text-[10px] text-tv-blue hover:bg-tv-blue/20"
                        >
                          + Add to Chart
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-col gap-1 border-t border-tv-border p-2">
                <button
                  type="button"
                  onClick={() => setEditingCustom('new')}
                  className="rounded bg-tv-blue px-2 py-1.5 text-xs text-white hover:bg-tv-blue/90"
                >
                  + New Indicator
                </button>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={handleExportCustom}
                    disabled={indicators.length === 0}
                    className="flex-1 rounded border border-tv-border px-2 py-1 text-[10px] text-tv-text-muted hover:text-tv-text-primary disabled:opacity-40"
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={() => importCustomRef.current?.click()}
                    className="flex-1 rounded border border-tv-border px-2 py-1 text-[10px] text-tv-text-muted hover:text-tv-text-primary"
                  >
                    Import
                  </button>
                  <input
                    ref={importCustomRef}
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleImportCustom}
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Pine Script list ────────────────────────────────────────── */}
          {tab === 'pine' && (
            <>
              <div className="relative min-h-0 flex-1 overflow-y-auto">
                {scripts.length === 0 && (
                  <p className="p-4 text-center text-[10px] text-tv-text-muted">
                    No scripts yet. Create one or load an example →
                  </p>
                )}
                {scripts.map((sc) => {
                  const isSelected = editingPine?.id === sc.id
                  return (
                    <div
                      key={sc.id}
                      className={cn(
                        'flex cursor-pointer flex-col gap-1 border-b border-tv-border/50 px-3 py-2 hover:bg-tv-bg-secondary',
                        isSelected && 'bg-tv-bg-secondary'
                      )}
                      onClick={() => setEditingPine(sc)}
                    >
                      <div className="flex items-center gap-1">
                        <span
                          className={`mr-1 h-2 w-2 shrink-0 rounded-full ${sc.enabled ? 'bg-tv-accent' : 'bg-tv-text-muted'}`}
                        />
                        <span className="min-w-0 flex-1 truncate text-xs text-tv-text-primary">
                          {sc.title}
                        </span>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            title={sc.enabled ? 'Disable' : 'Enable'}
                            onClick={(e) => {
                              e.stopPropagation()
                              togglePine(sc.id)
                            }}
                            className={`text-[10px] ${sc.enabled ? 'text-tv-green' : 'text-tv-text-muted'}`}
                          >
                            {sc.enabled ? '●' : '○'}
                          </button>
                          <button
                            type="button"
                            title="Duplicate"
                            onClick={(e) => {
                              e.stopPropagation()
                              const newId = duplicateScript(sc.id)
                              if (newId) {
                                const dup = usePineStore
                                  .getState()
                                  .scripts.find((x) => x.id === newId)
                                if (dup) setEditingPine(dup)
                              }
                            }}
                            className="text-[10px] text-tv-text-muted hover:text-tv-text-primary"
                          >
                            ⧉
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeScript(sc.id)
                              if (editingPine?.id === sc.id) setEditingPine(null)
                            }}
                            className="text-[10px] text-tv-text-muted hover:text-tv-red"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="rounded bg-tv-bg-secondary px-1 py-0.5 text-[9px] text-tv-text-muted">
                          {sc.overlay ? 'Overlay' : 'Pane'}
                        </span>
                      </div>
                      {onAttachToChart && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onAttachToChart(sc.id)
                          }}
                          className="mt-0.5 w-full rounded bg-tv-blue/10 px-2 py-0.5 text-[10px] text-tv-blue hover:bg-tv-blue/20"
                        >
                          + Add to Chart
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-col gap-1 border-t border-tv-border p-2">
                <div className="relative flex gap-1">
                  <button
                    type="button"
                    onClick={handleNewPine}
                    className="flex-1 rounded bg-tv-blue px-2 py-1.5 text-xs text-white hover:bg-tv-blue/90"
                  >
                    + New Script
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPineExamples((v) => !v)}
                    className="rounded border border-tv-border px-2 py-1.5 text-[10px] text-tv-text-muted hover:text-tv-text-primary"
                  >
                    Examples ▾
                  </button>
                  {showPineExamples && (
                    <div className="absolute bottom-full left-0 z-10 mb-1 w-full overflow-hidden rounded border border-tv-border bg-tv-bg-primary shadow-lg">
                      {PINE_EXAMPLES.map((ex) => (
                        <button
                          key={ex.id}
                          type="button"
                          onClick={() => handleLoadPineExample(ex.id)}
                          className="block w-full px-3 py-1.5 text-left text-[10px] text-tv-text-primary hover:bg-tv-bg-secondary"
                        >
                          {ex.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={handleExportPine}
                    disabled={scripts.length === 0}
                    className="flex-1 rounded border border-tv-border px-2 py-1 text-[10px] text-tv-text-muted hover:text-tv-text-primary disabled:opacity-40"
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={() => importPineRef.current?.click()}
                    className="flex-1 rounded border border-tv-border px-2 py-1 text-[10px] text-tv-text-muted hover:text-tv-text-primary"
                  >
                    Import
                  </button>
                  <input
                    ref={importPineRef}
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleImportPine}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Editor pane ──────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {tab === 'custom' && (
            <CustomIndicatorEditor indicator={selectedCustom} onClose={onClose} />
          )}
          {tab === 'pine' && (
            <PineScriptEditor
              script={editingPine}
              onSaved={(sc) => setEditingPine(sc)}
              updateScript={updateScript}
            />
          )}
        </div>
      </div>
    </div>
  )
}
